import React, { useCallback, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'

interface ContextDrawerProps {
  title: React.ReactNode
  icon?: React.ReactNode
  onClose: () => void
  darkMode?: boolean
  /** localStorage key used to persist the drawer width in px */
  storageKey?: string
  /** Render as an absolute overlay on the right edge instead of a flex sibling */
  overlay?: boolean
  closeLabel: string
  resizeLabel: string
  children: React.ReactNode
}

const MIN_WIDTH = 380

const clampWidth = (width: number) =>
  Math.min(Math.max(width, MIN_WIDTH), Math.max(MIN_WIDTH, Math.round(window.innerWidth * 0.7)))

const readStoredWidth = (storageKey?: string): number => {
  const fallback = Math.round(Math.min(window.innerWidth * 0.42, 720))
  if (!storageKey) return clampWidth(fallback)
  try {
    const raw = window.localStorage.getItem(storageKey)
    const parsed = raw ? parseInt(raw, 10) : NaN
    return clampWidth(Number.isFinite(parsed) ? parsed : fallback)
  } catch {
    return clampWidth(fallback)
  }
}

/**
 * Resizable right-side contextual drawer for tools like the Vehicle Control
 * Center. Width is adjusted by dragging the left edge (pointer events, bounds
 * 380px – 70vw) and persisted to localStorage. Below md it goes full-width.
 */
const ContextDrawer: React.FC<ContextDrawerProps> = ({
  title,
  icon,
  onClose,
  darkMode = false,
  storageKey,
  overlay = false,
  closeLabel,
  resizeLabel,
  children
}) => {
  const [width, setWidth] = useState<number>(() => readStoredWidth(storageKey))
  const [resizing, setResizing] = useState(false)
  const widthRef = useRef(width)
  widthRef.current = width

  const persistWidth = useCallback((value: number) => {
    if (!storageKey) return
    try { window.localStorage.setItem(storageKey, String(Math.round(value))) } catch { /* noop */ }
  }, [storageKey])

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    setResizing(true)
  }, [])

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!resizing) return
    setWidth(clampWidth(window.innerWidth - event.clientX))
  }, [resizing])

  const endResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!resizing) return
    try { event.currentTarget.releasePointerCapture(event.pointerId) } catch { /* noop */ }
    setResizing(false)
    persistWidth(widthRef.current)
  }, [resizing, persistWidth])

  // Keep width in bounds when the window shrinks
  useEffect(() => {
    const onWindowResize = () => setWidth(current => clampWidth(current))
    window.addEventListener('resize', onWindowResize)
    return () => window.removeEventListener('resize', onWindowResize)
  }, [])

  const border = darkMode ? 'border-gray-700' : 'border-gray-200'

  return (
    <aside
      className={`${overlay ? 'absolute inset-y-0 right-0 z-20' : 'relative'} flex min-h-0 flex-col border-l shadow-2xl max-md:!w-full ${darkMode ? 'bg-gray-900' : 'bg-white'} ${border}`}
      style={{ width }}
      role="complementary"
    >
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endResize}
        onPointerCancel={endResize}
        className={`absolute inset-y-0 left-0 z-10 hidden w-1.5 cursor-col-resize md:block ${resizing ? 'bg-blue-500/60' : 'hover:bg-blue-500/40'}`}
        role="separator"
        aria-orientation="vertical"
        aria-label={resizeLabel}
        title={resizeLabel}
      />
      <div className={`flex h-11 shrink-0 items-center justify-between border-b px-3 ${border}`}>
        <div className={`flex min-w-0 items-center gap-2 text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          {icon}<span className="truncate">{title}</span>
        </div>
        <button
          onClick={onClose}
          className={`rounded p-1.5 transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
          title={closeLabel}
          aria-label={closeLabel}
        >
          <X size={17} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
    </aside>
  )
}

export default ContextDrawer
